import confetti from '@hiseb/confetti';

/** Tons roxos da marca (#8200ff), no mesmo espírito do burst anterior. Padrão quando quem chama não passa cores. */
export const BRAND_CONFETTI_COLORS: string[] = ['#8200ff', '#6500c7', '#9d3cff', '#ac56ff', '#b873ff', '#8e63ff', '#a88aff', '#ccabff', '#d100ff'];

const CANVAS_MARK = 'data-agj-confetti';
const LIVE_MS = 4_000;

type TConfettiBurst = {
  canvas: HTMLCanvasElement;
  dispose: () => void;
  restart: () => void;
};

let liveBurst: TConfettiBurst | null = null;

function isConfettiCanvas(node: Element): node is HTMLCanvasElement {
  if (!(node instanceof HTMLCanvasElement)) return false;
  if (node.hasAttribute(CANVAS_MARK)) return true;
  return node.style.position === 'fixed' && node.style.pointerEvents === 'none';
}

function removeCanvas(canvas: HTMLCanvasElement): void {
  canvas.remove();
}

function adoptCanvas(canvas: HTMLCanvasElement): TConfettiBurst {
  canvas.setAttribute(CANVAS_MARK, '');
  canvas.setAttribute('aria-hidden', 'true');

  let done = false;
  let lifetime = 0;

  const observer = new MutationObserver(() => {
    if (canvas.isConnected) return;
    done = true;
    window.clearTimeout(lifetime);
    observer.disconnect();
    if (liveBurst?.canvas === canvas) liveBurst = null;
  });

  const dispose = (): void => {
    if (done) return;
    done = true;
    window.clearTimeout(lifetime);
    observer.disconnect();
    removeCanvas(canvas);
    if (liveBurst?.canvas === canvas) liveBurst = null;
  };

  const restart = (): void => {
    if (done) return;
    window.clearTimeout(lifetime);
    lifetime = window.setTimeout(dispose, LIVE_MS);
  };

  observer.observe(document.body, { childList: true });
  restart();

  return { canvas, dispose, restart };
}

export interface IConfettiOptions {
  fromCenter?: boolean;
  /** Cores do burst. O link público passa a paleta do acento da empresa (theme-confetti.utils.ts do Front). */
  colors?: string[];
}

function centerOfScreen(): { x: number; y: number } {
  const root = document.querySelector('.booking-confirmation');
  if (root instanceof HTMLElement) {
    const box = root.getBoundingClientRect();
    return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
  }

  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

/**
 * Dispara o burst e devolve um dispose. A lib remove o canvas ao cair a última partícula;
 * o timeout e o dispose cobrem o caso dela falhar, para o nó não ficar órfão no HTML.
 */
export function toggleConfetti(options: IConfettiOptions = {}): () => void {
  if (typeof window === 'undefined') return () => undefined;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return () => undefined;

  document.body.querySelectorAll(':scope > canvas').forEach((node) => {
    if (isConfettiCanvas(node) && liveBurst?.canvas !== node) removeCanvas(node);
  });

  const before = new Set(document.body.querySelectorAll(':scope > canvas'));

  confetti({
    color: options.colors?.length ? options.colors : BRAND_CONFETTI_COLORS,
    count: 120,
    size: 1,
    velocity: 220,
    fade: true,
    ...(options.fromCenter ? { position: centerOfScreen() } : {})
  });

  const existing = liveBurst?.canvas.isConnected ? liveBurst : null;
  if (existing) {
    existing.restart();
    return existing.dispose;
  }

  const created = [...document.body.querySelectorAll<HTMLCanvasElement>(':scope > canvas')].find((node) => !before.has(node));
  if (!created) return () => undefined;

  liveBurst = adoptCanvas(created);
  return liveBurst.dispose;
}
